/**
 * TR4 — pure, Lit-free audit-log grouping module (O2D-PRD-103).
 *
 * Groups already-loaded `AuditLog` rows by their `commandUid` so the viewer can
 * render one business-event line per command. Mirrors the mapping-module seam
 * (`audit-business-event.mapping.ts`): it imports NO Lit and NO view code — the
 * only imports are `import type` (erased at runtime), so this stays a portable,
 * standalone-testable pure module. It performs no translation and touches no DOM.
 *
 * The group's intent row (the business-event header source) is located by
 * PREDICATE (`action === 'COMMAND' && property === '__command__'`), NEVER by sort
 * position: `createdAt` is written at second granularity on both the COMMAND
 * intent row and its diff rows, and the repository sorts only `createdAt desc`
 * with no tie-breaker (`audit-log.repository.ts:34-39`), so the intent row can
 * land anywhere within its group after sorting.
 *
 * No-silent-loss invariant (Failure Conditions / G3): every input row ends up in
 * exactly one group — as a header row or a detail row. Command-less rows and
 * commandUid groups without an intent row are kept and marked technical (LOUD),
 * never dropped.
 */

import type { Entity } from '../domain/entities/entity';
import type { AuditLog } from '../domain/entities/audit-log.entity';

/** Marker value written on the intent row's `action` column by O2D-PRD-102. */
const COMMAND_INTENT_ACTION = 'COMMAND';
/** Marker value written on the intent row's `property` column by O2D-PRD-102. */
const COMMAND_INTENT_PROPERTY = '__command__';

/**
 * One rendered unit of the grouped audit view.
 *
 * - Business-event group: `commandUid` set, `headerRow` = the COMMAND intent row,
 *   `detailRows` = its raw property-diff rows (intent row excluded), `isTechnical`
 *   = false.
 * - Technical group (command-less row, or a commandUid group with no intent row):
 *   `headerRow` = null, all its rows kept in `detailRows`, `isTechnical` = true —
 *   rendered raw + marked "technischer Eintrag".
 */
export interface AuditLogGroup<TEntity extends Entity<TEntity>> {
    /** The command that produced this group, or `null` for command-less rows. */
    commandUid: string | null;
    /** The COMMAND intent row (header source), or `null` when there is none. */
    headerRow: AuditLog<TEntity> | null;
    /** The raw property-diff rows of the group (the intent row is excluded). */
    detailRows: AuditLog<TEntity>[];
    /** True → render raw + marked technical (no business-event header exists). */
    isTechnical: boolean;
}

/**
 * Is this row the command's intent row? Located by predicate, never by position.
 */
function isCommandIntentRow<TEntity extends Entity<TEntity>>(row: AuditLog<TEntity>): boolean {
    return row.action === COMMAND_INTENT_ACTION && row.property === COMMAND_INTENT_PROPERTY;
}

/**
 * Does this row belong to a command? A null / undefined / empty `commandUid`
 * (pre-102 data, non-command writes) counts as command-less.
 */
function hasCommandUid<TEntity extends Entity<TEntity>>(row: AuditLog<TEntity>): boolean {
    return !!row.commandUid;
}

/**
 * Factory: a fresh, provisionally-technical group for a `commandUid`. It flips to
 * a business-event once an intent row is placed into it (see {@link placeRow}) —
 * the intent row can arrive after some diff rows (non-deterministic sort order),
 * so the group starts technical and is upgraded, never the reverse.
 *
 * Kept as a distinct helper so "what a new group looks like" stays separate from
 * the routing loop (mirrors the mapping module's `buildCommandKey` seam).
 */
function newCommandGroup<TEntity extends Entity<TEntity>>(commandUid: string): AuditLogGroup<TEntity> {
    return { commandUid, headerRow: null, detailRows: [], isTechnical: true };
}

/**
 * Route one row into its (already-created) group. The FIRST intent row becomes
 * the header and upgrades the group to a business-event; every other row — a diff
 * row, or a stray second intent row — is kept as detail so no row is ever
 * silently dropped (no-silent-loss invariant / G3).
 *
 * Kept distinct from the iteration loop so the header-vs-detail DECISION is a
 * single-responsibility unit, testable in isolation from grouping.
 */
function placeRow<TEntity extends Entity<TEntity>>(
    group: AuditLogGroup<TEntity>,
    row: AuditLog<TEntity>
): void {
    if (group.headerRow === null && isCommandIntentRow(row)) {
        group.headerRow = row;
        group.isTechnical = false;
    } else {
        group.detailRows.push(row);
    }
}

/**
 * Group audit rows by `commandUid` into business-event / technical groups.
 *
 * Groups appear in first-seen order. Command-less rows each form their own
 * single-row technical group. A commandUid group is a business-event IFF it
 * contains a COMMAND intent row; otherwise every row in it is kept as technical.
 *
 * @param logs the already-loaded, filtered audit rows.
 * @returns the ordered groups; `[]` for empty input.
 */
export function groupAuditLogs<TEntity extends Entity<TEntity>>(
    logs: AuditLog<TEntity>[]
): AuditLogGroup<TEntity>[] {
    const groups: AuditLogGroup<TEntity>[] = [];
    const groupByCommandUid = new Map<string, AuditLogGroup<TEntity>>();

    for (const row of logs) {
        if (!hasCommandUid(row)) {
            // Command-less → own single-row technical group (LOUD, never merged).
            groups.push({ commandUid: null, headerRow: null, detailRows: [row], isTechnical: true });
            continue;
        }

        let group = groupByCommandUid.get(row.commandUid);
        if (!group) {
            group = newCommandGroup(row.commandUid);
            groupByCommandUid.set(row.commandUid, group);
            groups.push(group);
        }

        placeRow(group, row);
    }

    return groups;
}
